import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Users } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { mediaUrl } from "@/lib/media";
import { teamDe } from "@/lib/teamNames";
import { GROUP_PRIZES, SINGLE_PRIZE } from "@/lib/constants";
import { loadTopRanking, loadGroupRankings, type RankRow, type GroupRankingRow } from "@/lib/ranking";
import type { BonusTruth } from "@/hooks/useTournamentFinal";

// End-of-tournament hero. Shown only once the bonus questions are resolved
// (see useTournamentFinal) — at that point the standings are final and the
// two prize winners are decided:
//   • Einzelwertung #1  → SINGLE_PRIZE (Wellnessurlaub für 2)
//   • Tippgruppe   #1  → GROUP_PRIZES[0] (3.000 € Sponsoring)
// Places 2–3 are shown for both so the whole podium gets its moment.

export function ChampionsSpotlight({
  truth,
  currentUserId,
}: {
  truth: BonusTruth | null;
  currentUserId?: string | null;
}) {
  const [solo, setSolo] = useState<RankRow[]>([]);
  const [groups, setGroups] = useState<GroupRankingRow[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [top, gRanks] = await Promise.all([loadTopRanking(3), loadGroupRankings()]);
      if (!alive) return;
      setSolo(top.rows.slice(0, 3));
      setGroups(gRanks.slice(0, 3));
    })();
    return () => { alive = false; };
  }, []);

  const champ = solo[0] ?? null;
  const groupChamp = groups[0] ?? null;
  const worldChampion = truth?.worldChampion ?? null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      {/* Banner */}
      <div className="relative overflow-hidden rounded-lg bg-hero text-primary-foreground shadow-pop p-5 text-center">
        <div className="absolute -right-8 -top-8 text-[140px] opacity-10 select-none">🏆</div>
        <div className="absolute -left-10 -bottom-10 text-[120px] opacity-10 select-none">🎉</div>
        <div className="relative">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground/80">
            WM 2026 · Abgepfiffen
          </div>
          <h1 className="font-display font-extrabold text-2xl mt-1 leading-tight">
            Das Tippspiel ist entschieden!
          </h1>
          {worldChampion && (
            <div className="mt-3 inline-flex items-center gap-2 bg-primary-foreground/15 backdrop-blur rounded-pill px-3.5 py-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wider opacity-80">Weltmeister</span>
              <span className="font-display font-extrabold text-base">{teamDe(worldChampion)}</span>
            </div>
          )}
        </div>
      </div>

      {/* The two champions */}
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Einzelsieger */}
        <div className="rounded-lg bg-gold shadow-pop p-4 space-y-3">
          <div className="flex items-center gap-1.5">
            <Trophy className="h-4 w-4" />
            <h2 className="font-display font-extrabold text-base leading-none">Gesamtsieger:in</h2>
          </div>
          {champ ? (
            <>
              <div className="flex items-center gap-3">
                <UserAvatar url={champ.avatar_url} emoji={champ.emoji} size={52} />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-extrabold text-lg truncate leading-tight">
                    {champ.username}
                    {currentUserId === champ.user_id && (
                      <span className="ml-1.5 text-[10px] font-extrabold bg-foreground/15 rounded-pill px-1.5 py-0.5 align-middle">DU</span>
                    )}
                  </div>
                  <div className="text-[12px] font-bold tabular-nums opacity-80">{champ.points} Punkte</div>
                </div>
                <div className="text-3xl shrink-0">🥇</div>
              </div>
              <div className="rounded-md bg-foreground/10 p-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Hauptpreis</div>
                <div className="text-sm font-bold leading-tight">
                  {SINGLE_PRIZE.icon} {SINGLE_PRIZE.title}
                </div>
                <div className="text-[11px] opacity-80 leading-tight">
                  {SINGLE_PRIZE.hotel} · {SINGLE_PRIZE.location}
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm font-bold opacity-70">Noch keine Wertung.</div>
          )}
          <Podium
            rows={solo.slice(1, 3).map((r, i) => ({
              key: r.user_id,
              rank: i + 2,
              label: r.username,
              points: r.points,
              mine: currentUserId === r.user_id,
            }))}
          />
        </div>

        {/* Siegergruppe */}
        <div className="rounded-lg bg-gold shadow-pop p-4 space-y-3">
          <div className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            <h2 className="font-display font-extrabold text-base leading-none">Siegergruppe</h2>
          </div>
          {groupChamp ? (
            <>
              <Link to={`/gruppe/${groupChamp.slug}`} className="flex items-center gap-3 tap">
                {groupChamp.logo_url ? (
                  <img
                    src={mediaUrl(groupChamp.logo_url)}
                    alt=""
                    className="h-[52px] w-[52px] rounded-md object-cover shrink-0 bg-foreground/10"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  />
                ) : (
                  <div className="h-[52px] w-[52px] rounded-md bg-foreground/10 flex items-center justify-center text-2xl shrink-0">🏆</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display font-extrabold text-lg truncate leading-tight">{groupChamp.name}</div>
                  <div className="text-[12px] font-bold tabular-nums opacity-80">
                    {groupChamp.total_points} Punkte · {groupChamp.member_count} Tipper
                  </div>
                </div>
                <div className="text-3xl shrink-0">🥇</div>
              </Link>
              <div className="rounded-md bg-foreground/10 p-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Sponsoring</div>
                <div className="text-sm font-bold leading-tight tabular-nums">{GROUP_PRIZES[0].label}</div>
                <div className="text-[11px] opacity-80 leading-tight">von Englputzeder</div>
              </div>
            </>
          ) : (
            <div className="text-sm font-bold opacity-70">Noch keine Tippgruppe gewertet.</div>
          )}
          <Podium
            rows={groups.slice(1, 3).map((g, i) => ({
              key: g.group_id,
              rank: i + 2,
              label: g.name,
              points: g.total_points,
              suffix: GROUP_PRIZES[i + 1]?.short,
            }))}
          />
        </div>
      </div>
    </motion.section>
  );
}

function Podium({
  rows,
}: {
  rows: { key: string; rank: number; label: string; points: number; mine?: boolean; suffix?: string }[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1 pt-1 border-t border-foreground/15">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-[12px]">
          <span className="shrink-0">{r.rank === 2 ? "🥈" : "🥉"}</span>
          <span className="flex-1 min-w-0 truncate font-bold">
            {r.label}
            {r.mine && (
              <span className="ml-1.5 text-[9px] font-extrabold bg-foreground/15 rounded-pill px-1 py-0.5 align-middle">DU</span>
            )}
          </span>
          {r.suffix && <span className="text-[10px] font-bold opacity-70 shrink-0 tabular-nums">{r.suffix}</span>}
          <span className="tabular-nums font-extrabold shrink-0 opacity-80">{r.points}</span>
        </div>
      ))}
    </div>
  );
}
